import { useState, useEffect } from 'react';
import './index.css';

const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
interface ManualVideoFormProps {
  manualLink: string;
  setManualLink: (value: string) => void;
  handleAddManualVideo: (title: string, url: string) => void;
  message: string | null;
  isLoading?: boolean;
}

const ManualVideoForm = ({
  manualLink,
  setManualLink,
  handleAddManualVideo,
  message,
  isLoading = false,
}: ManualVideoFormProps) => {
  const [fetchedTitle, setFetchedTitle] = useState('');
  const [loadingTitle, setLoadingTitle] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const isValidYouTubeUrl = (url: string) => {
    const pattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return pattern.test(url);
  };

  const extractVideoId = (url: string) => {
    const match = url.match(
      /(?:youtube\.com\/.*[?&]v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/watch\?v=)([^&?#]+)/,
    );
    return match ? match[1] : null;
  };

  const fetchVideoTitle = async (videoId: string) => {
    if (!videoId) return;
    setLoadingTitle(true);

    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&part=snippet&key=${YOUTUBE_API_KEY}`,
      );
      const data = await response.json();
      if (data.items && data.items.length > 0) {
        setFetchedTitle(data.items[0].snippet.title);
      } else {
        setFetchedTitle('Title not found');
      }
    } catch (error) {
      setFetchedTitle('Error fetching title');
    } finally {
      setLoadingTitle(false);
    }
  };

  useEffect(() => {
    if (isValidYouTubeUrl(manualLink)) {
      const videoId = extractVideoId(manualLink);
      if (videoId) {
        fetchVideoTitle(videoId);
      } else {
        setFetchedTitle('');
      }
    } else {
      setFetchedTitle('');
    }
  }, [manualLink]);

  const isFormValid = fetchedTitle.trim() !== '' && isValidYouTubeUrl(manualLink);

  return (
    <div className={`manual-video-form ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className='form-header' onClick={() => setIsExpanded(!isExpanded)}>
        <h2 className='form-title'>Add a Video Manually</h2>
        <span className='toggle-icon'>{isExpanded ? '−' : '+'}</span>
      </div>

      {isExpanded && (
        <div className='form-content'>
          <div className='form-group'>
            <div className='input-group'>
              <label htmlFor='video-url'>YouTube URL</label>
              <input
                id='video-url'
                type='text'
                placeholder='https://www.youtube.com/watch?v=...'
                value={manualLink}
                onChange={e => setManualLink(e.target.value)}
                className='form-input'
                disabled={isLoading}
              />
              {manualLink && !isValidYouTubeUrl(manualLink) && (
                <p className='input-error'>Please enter a valid YouTube URL</p>
              )}
            </div>

            {loadingTitle && <div className='title-loading'>Fetching video info...</div>}
            {fetchedTitle && !loadingTitle && (
              <div className='video-title-preview'>
                <strong>Video:</strong> {fetchedTitle}
              </div>
            )}
          </div>

          <div className='form-actions'>
            <button
              type='button'
              onClick={() => handleAddManualVideo(fetchedTitle, manualLink)}
              className='submit-button'
              disabled={!isFormValid || isLoading}>
              {isLoading ? (
                <span className='button-loading'>
                  <span className='spinner' /> Adding...
                </span>
              ) : (
                'Add Video'
              )}
            </button>
          </div>

          {message && (
            <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManualVideoForm;
